import {useCallback} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {conversationApi} from '../api';
import {ConversationSeenState} from '../model';
import {ConversationAudience} from './useConversationMessages';

export interface UseConversationSeenResult {
  seenState: ConversationSeenState | undefined;
  // Признак того, что `seenState` — успешный ответ сервера, полученный в этот
  // заход, а не отданный синхронно кэш. Нужен потребителю, который снимает с
  // состояния одноразовый снимок и потом уже не пересматривает его.
  isSeenStateFresh: boolean;
  // Признак того, что запрос состояния в этот заход завершился — успехом или
  // отказом — и больше ничего не ждёт. Отличается от свежести: по отказу
  // перечитанного состояния нет, но и ждать его больше незачем.
  isSeenStateSettled: boolean;
  isLoading: boolean;
  isError: boolean;
  markSeen: (seenUpTo: string) => void;
  isMarking: boolean;
  isMarkError: boolean;
}

// Адресат подтверждения передаётся аргументами мутации, а не берётся из
// замыкания. Точка монтирования Ленты компонент при смене Заявки не
// пересоздаёт, поэтому наблюдатель мутации живёт дольше одной переписки, а
// TanStack Query подменяет параметры уже запущенной мутации на параметры
// текущего рендера. С замыканием повтор после сетевой осечки ушёл бы уже на
// следующую переписку: отметка первой потерялась бы, а вторая оказалась бы
// прочитанной по чужой границе и без положенной выдержки присутствия.
interface MarkSeenVariables {
  appealId: string;
  audience: ConversationAudience;
  seenUpTo: string;
}

const seenQueryKey = (appealId: string, audience: ConversationAudience) =>
  ['conversation', appealId, 'seen', audience] as const;

export const useConversationSeen = (
  appealId: string,
  audience: ConversationAudience,
): UseConversationSeenResult => {
  const queryClient = useQueryClient();
  const queryKey = seenQueryKey(appealId, audience);

  const query = useQuery<ConversationSeenState, Error>({
    queryKey,
    queryFn: () => {
      if (audience === 'admin') {
        return conversationApi.getAdminSeenState(appealId);
      }
      return conversationApi.getRepSeenState(appealId);
    },
    // Общий пятиминутный `staleTime` из App.tsx здесь вреден: отметка
    // принадлежит Стороне, и её мог сдвинуть коллега из другой сессии. По
    // кэшу повторно открытая лента взяла бы устаревшую границу и нарисовала
    // «Новые сообщения» над уже прочитанным. Состояние читается заново при
    // каждом открытии и при возврате фокуса на вкладку.
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const mutation = useMutation<void, Error, MarkSeenVariables>({
    mutationFn: ({appealId: targetAppealId, audience: targetAudience, seenUpTo}) => {
      if (targetAudience === 'admin') {
        return conversationApi.markAdminSeen(targetAppealId, seenUpTo);
      }
      return conversationApi.markRepSeen(targetAppealId, seenUpTo);
    },
    // Глобальный retry из App.tsx задан только для запросов. Без повтора одна
    // сетевая осечка теряла бы отметку молча: условия присутствия после неудачи
    // не меняются, и лента второй попытки уже не сделает.
    retry: 2,
    retryDelay: 1000,
    onSuccess: (_data, variables) => {
      // Кэш и списки обновляются по адресату самой отправки: ответ может
      // прийти, когда открыта уже другая переписка.
      queryClient.setQueryData<ConversationSeenState>(
        seenQueryKey(variables.appealId, variables.audience),
        (prev) => ({
          // Подтверждения могут завершиться не в порядке отправки: у мутации
          // есть повторы, и запоздавший ответ ранней отправки откатил бы кэш
          // назад. Сервер применяет отметку монотонно (`max` с текущей) —
          // кэш обязан вести себя так же.
          seenAt: prev?.seenAt && prev.seenAt > variables.seenUpTo ? prev.seenAt : variables.seenUpTo,
          lastMessageAt: prev?.lastMessageAt ?? null,
          hasUnreadMessages: false,
        }),
      );

      if (variables.audience === 'admin') {
        queryClient.invalidateQueries({queryKey: ['conversation', 'admin', 'list'], refetchType: 'all'});
      } else {
        queryClient.invalidateQueries({queryKey: ['my-applications'], refetchType: 'all'});
      }
    },
  });

  const {mutate} = mutation;
  // Ссылка постоянна в пределах одной переписки — потребитель кладёт её в
  // зависимости эффекта присутствия, и таймер от лишних рендеров не сбрасывается.
  const markSeen = useCallback(
    (seenUpTo: string) => mutate({appealId, audience, seenUpTo}),
    [mutate, appealId, audience],
  );

  const markVariables = mutation.variables;

  return {
    seenState: query.data,
    // `refetchOnMount: 'always'` только запускает перезапрос — данные из кэша
    // React Query отдаёт синхронно, ещё до ответа сервера. Свежесть поднимается
    // только по успешному обновлению состояния в этот заход: при отказе
    // перезапроса `data` остаётся прежним кэшем, и признать его перечитанным
    // значило бы взять ровно ту устаревшую отметку, ради которой запрос и
    // делается. Ожидание конца самого запроса отсекает случай, когда счётчик
    // обновлений сдвинула запись кэша после подтверждения просмотра, а ответ
    // сервера ещё в пути.
    isSeenStateFresh: query.isSuccess && query.isFetchedAfterMount && !query.isFetching,
    // Завершённость поднимается и по отказу: перечитанного состояния нет, но
    // потребитель, который иначе ждал бы ответа вечно, вправе действовать по
    // тому, что у него есть.
    isSeenStateSettled: query.isFetchedAfterMount && !query.isFetching,
    isLoading: query.isLoading,
    isError: query.isError,
    markSeen,
    // Наблюдатель мутации переживает смену переписки, поэтому исход отправки
    // отдаётся только её собственному адресату: иначе отказ по прошлой Заявке
    // выглядел бы отказом по текущей и вызвал бы лишнее перепланирование.
    isMarking:
      mutation.isPending && markVariables?.appealId === appealId && markVariables.audience === audience,
    isMarkError:
      mutation.isError && markVariables?.appealId === appealId && markVariables.audience === audience,
  };
};
